/**
 * ## ItemsProviderWithStore.tsx ##
 * This file contains ItemsProviderWithStore component
 * @packageDocumentation
 */
import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { Dispatch } from 'redux';

import { TypeKeys, ItemsState } from '@common/react/store/ItemsProviderStore';
import {
	List,
	ItemsProvider,
	ItemsProviderProps,
	useItemsProviderContext,
	WithKey,
	SortingDirection,
	ItemsProviderContextActions,
	createItemsProviderContext,
	ItemsProviderContext,
	PaginationOptions,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import { equal } from '@common/typescript/Utils';
import AdvancedItemsProvider from '@common/react/components/Core/AdvancedItemsProvider/AdvancedItemsProvider';
import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import { BaseParams } from '@common/typescript/objects/BaseParams';
import { NotificationAction } from '@common/typescript/objects/NotificationAction';

export interface ItemsProviderWithStoreContext<T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<T> = List<T>> extends ItemsProviderContext<T, TFilter, TResponse,
		ItemsProviderWithStoreContextActions<T, TFilter, TResponse>> {}

interface ItemsHandlerProps<T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<T> = List<T>> extends ItemsProviderProps<T, TFilter, TResponse> {
	children: React.ReactNode | ((context: ItemsProviderWithStoreContext<T, TFilter, TResponse>) => React.ReactNode);
	storeName: string;
	initLoad: boolean;
	storeData: ItemsState<T>;
	storeHandler?: (data: HandleNotificationType<T>, dispatch: Dispatch) => void;
	storeSortAfterAdd?: (items: Array<T>) => Array<T>;
	syncStoreItems?: boolean;
}

export interface ItemsProviderWithStoreProps<T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<T> = List<T>> extends ItemsProviderProps<T, TFilter, TResponse> {
	/**
	 * - 1. ReactElement to be wrapped in an ItemsProviderWithStore context
	 * - 2. function with ItemsProviderWithStore state as first argument
	 */
	children: React.ReactNode | ((context: ItemsProviderWithStoreContext<T, TFilter, TResponse>) => React.ReactNode);
	/**
	 * property in redux that stores data for ItemsProviderWithStore.
	 */
	storeName: string;
	/**
	 * default element sorting. Used to define the position of a new element.
	 *
	 * (only for AdvancedItemsProvider)
	 *
	 * If defaultSort and filterHandler are present, AdvancedItemsProvider will be used.
	 */
	defaultSort?: [string, SortingDirection];
	/**
	 * default element sorting. Determines whether a new element should be displayed with the current filters
	 *
	 * (only for AdvancedItemsProvider)
	 *
	 * If defaultSort and filterHandler are present, AdvancedItemsProvider will be used.
	 */
	filterHandler?: (item: T, filters: TFilter) => boolean | Promise<boolean>;
	/**
	 * Custom sorting of elements after adding a new one.
	 *
	 * (only for AdvancedItemsProvider)
	 *
	 * If defaultSort and filterHandler are present, AdvancedItemsProvider will be used.
	 */
	sortHandler?: (item1: T, item2: T, filters: TFilter) => number;
	/**
	 * init load option
	 */
	skipInitLoad?: boolean;
	/**
	 * the value from the store will be used directly and not just the starting state
	 */
	syncStoreItems?: boolean;
	/**
	 * custom function to update store after calling handleNotification from context
	 * @param data     - HandleNotificationType<T>
	 * @param dispatch - store dispatch callback
	 */
	storeHandler?: (data: HandleNotificationType<T>, dispatch: Dispatch) => void;
	/**
	 * custom function to update store after calling handleNotification from context
	 * @param items     - update items after add item
	 */
	storeSortAfterAdd?: (items: Array<T>) => Array<T>;
}

const getParamsFromItemProvider = <TFilter extends BaseParams, >(filters: TFilter, pagination?: PaginationOptions) => {
	return {
		...filters,
		...{
			count: pagination?.pageSize || 10,
			current: filters?.page || pagination?.current || 1,
			pageSize: undefined,
			page: undefined,
		},
	};
};

const getByIdOrFirst = <T extends WithKey, >(id: number | undefined, items: Array<T>) => {
	return id ? items?.find((item) => item.id === id) ?? items[0] : items[0];
};

interface NotificationAdd<T extends WithKey> {
	value: T;
	action: NotificationAction.Add;
}

interface NotificationRemove<T extends WithKey> {
	value: number;
	action: NotificationAction.Delete;
}

interface NotificationUpdate<T extends WithKey> {
	value: T;
	action: NotificationAction.Update;
}

type HandleNotificationType<T extends WithKey> = NotificationAdd<T> | NotificationRemove<T> | NotificationUpdate<T>;

export interface ItemsProviderWithStoreContextActions<T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<T> = List<T>> extends ItemsProviderContextActions<T, TFilter, TResponse> {
	handleNotification?: (data: HandleNotificationType<T>) => void;
	getByIdOrFirst: (id?: number) => T | undefined;
}

export const useItemsProviderWithStoreContext = <T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<T> = List<T>>(
		required?: boolean,
	) => useItemsProviderContext<T, TFilter, TResponse, ItemsProviderWithStoreContextActions<T, TFilter, TResponse>>(required);

const ItemsHandler = <T extends WithKey, TFilter extends BaseParams = BaseParams, TResponse extends List<T> = List<T>>({
	storeName, initLoad, storeData, children, storeSortAfterAdd, syncStoreItems, ...p
}: ItemsHandlerProps<T, TFilter, TResponse>) => {
	const ItemsContext = createItemsProviderContext<T, TFilter, TResponse,
		ItemsProviderWithStoreContextActions<T, TFilter, TResponse>>();
	const context = useItemsProviderContext<T, TFilter, TResponse>();

	const {
		state: {
			items,
			filters,
			pagination,
			error,
		},
		actions: { load },
	} = context;
	const [initUpdate, setInitUpdate] = React.useState(true);

	const dispatch = useDispatch();
	const {
		storeHandler = ({ value, action }, dispatch) => {
			if (action === NotificationAction.Add) {
				dispatch({
					type: TypeKeys.ADDITEM,
					storageName: storeName,
					objectType: storeName,
					value,
					sort: storeSortAfterAdd,
				});
			} else if (action === NotificationAction.Delete) {
				dispatch({
					type: TypeKeys.REMOVEITEM,
					storageName: storeName,
					objectType: storeName,
					paramName: 'id',
					value,
				});
			} else if (action === NotificationAction.Update) {
				dispatch({
					type: TypeKeys.UPDATEITEM,
					storageName: storeName,
					objectType: storeName,
					paramName: 'id',
					value,
				});
			}
		},
	} = p;

	React.useEffect(() => {
		if (!syncStoreItems && initLoad) {
			load({})
				.catch((error) => {
					if (typeof error === 'string' && error.includes('aborted')) {
						return;
					}
					load({})
						.catch((e) => {
							console.log(e);
						});
				});
		}
	}, []);

	React.useEffect(() => {
		if (!syncStoreItems) {
			if ((!initUpdate || (storeData?.isEmpty && !initLoad))) {
				dispatch({
					items,
					params: getParamsFromItemProvider({ ...p.unhandledFilters, ...filters }, pagination),
					type: TypeKeys.SETITEMS,
					storageName: storeName,
					objectType: p.loadRequest ?? storeName,
					total: pagination.total || items?.length,
					current: pagination.current,
					isEmpty: !!error,
				});
			}
			setInitUpdate(false);
		}
	}, [items, error]);

	const value: ItemsProviderWithStoreContext<T, TFilter, TResponse> = React.useMemo(() => ({
		...context,
		actions: {
			...context.actions,
			getByIdOrFirst: (id?: number) => getByIdOrFirst(id, items),
			handleNotification: syncStoreItems ? undefined : (data: HandleNotificationType<T>) => {
				storeHandler(data, dispatch);
			},
		},
	}), [context]);

	return (
		<ItemsContext.Provider
			value={value}
		>
			{typeof children === 'function' ? children(value) : children}
		</ItemsContext.Provider>
	);
};

/**
 * ItemsProviderWithStore component.
 *
 * @example
 * <ItemsProviderWithStore<T> loadRequest="request">{React.ReactNode}</ItemsProvider>
 *
 * @example
 * <ItemsProviderWithStore<T> loadRequest="request">{(context) => React.ReactNode}</ItemsProvider>
 * @example
 * <ItemsProviderWithStore<T> loadRequest="request" render={(item, props) => SingleItem}/>
 *
 * @example if you need to use the AdvancedItemsProvider, you must provide a defaultSort and filterHandler:
 * <ItemsProviderWithStore<T>
 *   loadRequest="request"
 *   defaultSort={['sortOrder', SortingDirection.Ascending]}
 *   filterHandler={() => false}
 * >
 *   {(context) => React.ReactNode}
 * </ItemsProvider>
 *
 * @typeParam T - T Any {@link WithKey}
 * @param props - {@link ItemsProviderWithStoreProps}
 * @type {React.FC<ItemsProviderWithStoreProps>}
 * @returns React.ReactElement
 */
export const ItemsProviderWithStore: <T extends WithKey, TFilter extends BaseParams = BaseParams, TResponse extends List<T> = List<T>>(
	p: ItemsProviderWithStoreProps<T, TFilter, TResponse>
) => React.ReactElement<T> = <T extends WithKey, TFilter extends BaseParams = BaseParams, TResponse extends List<T> = List<T>>(
	props: ItemsProviderWithStoreProps<T, TFilter, TResponse>,
) => {
	const {
		children,
		syncStoreItems = true,
		storeName,
		defaultSort,
		filterHandler,
		sortHandler,
		skipInitLoad = false,
		storeHandler,
		storeSortAfterAdd = sortHandler ? (items) => items.sort((a, b) => sortHandler(a, b, {} as TFilter)) : undefined,
		...p
	} = props;
	const store = useSelector((state) => state[storeName], shallowEqual) as ItemsState<T>;
	const context = useItemsProviderContext<T>(false);
	const loading = context?.state?.loadRequest === p.loadRequest && (context?.state?.loading || store?.isEmpty);
	const dispatch = useDispatch();

	const itemsData = React.useMemo(() => {
		const data = { items: p.items ?? store.items, pagination: p.pagination, fromStore: skipInitLoad };
		if (!store || skipInitLoad) {
			return data;
		}
		const currentParams = getParamsFromItemProvider({ ...p.unhandledFilters, ...p.filters }, p.pagination);
		const storeParams = { ...store.params, count: store.params.count || 10, current: store.params.current || 1 };

		return currentParams.count === storeParams.count && currentParams.count === storeParams.count
		&& equal(
			{ ...currentParams, count: undefined, current: undefined },
			{ ...storeParams, count: undefined, current: undefined },
		)
			? { items: p.items || store.items, pagination: p.pagination || store.pagination, fromStore: !store.isEmpty || !!p.items } : data;
	}, [!loading]);

	if (loading) {
		return <Loader />;
	}

	const onItemsChange = (items: Array<T>, filters: TFilter, res?: TResponse) => {
		if (!syncStoreItems) return;
		dispatch({
			items,
			params: getParamsFromItemProvider(filters, { current: filters?.current }),
			type: TypeKeys.SETITEMS,
			storageName: storeName,
			objectType: p.loadRequest ?? storeName,
			total: res?.count || items?.length,
			current: filters?.current,
			isEmpty: false,
		});
	};

	const inner = () => <>
		<ItemsHandler<T, TFilter, TResponse>
			storeData={store}
			{...p}
			storeName={storeName}
			initLoad={!(itemsData.fromStore || p.items)}
			storeHandler={storeHandler}
			storeSortAfterAdd={storeSortAfterAdd}
			syncStoreItems={syncStoreItems}
		>
			{children}
		</ItemsHandler>
	</>;

	React.useEffect(() => {
		if (syncStoreItems && p.items && store?.isEmpty) {
			dispatch({
				items: p.items,
				params: getParamsFromItemProvider({ ...p.unhandledFilters, ...p.filters }, p.pagination || { pageSize: 10, current: 1 }),
				type: TypeKeys.SETITEMS,
				storageName: storeName,
				objectType: p.loadRequest ?? storeName,
				total: p.items?.length,
				current: p.pagination?.current ?? 10,
				isEmpty: false,
			});
		}
	}, []);

	const itemsProviderProps = {
		items: itemsData.items,
		useSyncItemsInsteadHook: syncStoreItems,
		syncItems: syncStoreItems ? store.items : undefined,
		onItemsChange,
		skipInitLoad: syncStoreItems ? !!(itemsData.fromStore || p.items) : undefined,
	};

	return (
		!!defaultSort && !!filterHandler
			? (
				<AdvancedItemsProvider<T, TFilter, TResponse>
					defaultSort={defaultSort}
					filterHandler={filterHandler}
					sortHandler={sortHandler}
					{...p}
					{...itemsProviderProps}
					pagination={{ ...itemsData.pagination, current: itemsData.pagination?.current || 1 }}
				>
					{() => inner()}
				</AdvancedItemsProvider>
			)
			: (
				<ItemsProvider<T, TFilter, TResponse>
					{...p}
					{...itemsProviderProps}
					pagination={itemsData.pagination}
				>
					{() => inner()}
				</ItemsProvider>
			)
	);
};

export default ItemsProviderWithStore;
